import { Component, Element, Host, h, Listen, Prop, Method, Watch, State, Event, EventEmitter } from '@stencil/core';
import { handleButtonKeydown } from "../../utils/utils";

@Component({
  tag: 'me-accordion-item',
  styleUrl: 'me-accordion-item.scss',
  shadow: false,
})
export class MeAccordionItem {
  private static nextAccordionItemID = 1;
  private readonly accordionItemID = MeAccordionItem.nextAccordionItemID++;

  @Element() host: HTMLMeAccordionItemElement;

  @Prop({ mutable: true, reflect: true }) isCollapsed = true;

  @Event() meAccordionItemToggle: EventEmitter<HTMLMeAccordionItemElement>;

  @State() private isAnimated = false;

  @State() private iconPosition: 'aside' | 'inline' = 'aside';

  private themeIconPositionMap = {
    condensed: 'inline'
  } as const;

  private content: HTMLDivElement;
  private contentSlot: HTMLElement;

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    if (target.getAttribute('role') === 'button') {
      handleButtonKeydown(event, target);
    }
  }

  @Listen('meAccordionThemeChange', { target: 'body' })
  handleMeAccordionThemeChange(event: CustomEvent<HTMLMeAccordionElement>) {
    const accordion = this.host.closest('me-accordion');
    if (event.detail !== accordion) { return; }

    this.iconPosition = this.themeIconPositionMap[accordion.theme] ?? 'aside';
  }

  @Method()
  async toggle() {
    this.isCollapsed = !this.isCollapsed;
    this.meAccordionItemToggle.emit(this.host);
  }

  @Method()
  async collapse() {
    this.isCollapsed = true;
  }

  @Watch('isCollapsed')
  handleIsCollapsedChange(newValue: boolean, oldValue: boolean) {
    if (newValue === oldValue) { return; }

    this.updateContentMaxHeight();
  }

  componentWillLoad() {
    this.contentSlot = this.host.querySelector('[slot="content"]');
  }

  componentDidLoad() {
    this.updateContentMaxHeight();
    this.enableAnimations();
  }

  render() {
    const labelID = `me-accordion-item-label-${this.accordionItemID}`;
    const classList = [
      'me-accordion-item',
      this.isCollapsed ? 'me-accordion-item--collapsed' : null,
      this.isAnimated ? 'me-accordion-item--animated' : null,
    ].filter(c => !!c).join(' ');

    return (
      <Host class={classList}>
        <div class="me-accordion-item__main">
          <div
            id={labelID}
            class="me-accordion-item__header"
            role={this.contentSlot ? 'button' : null}
            onClick={this.contentSlot ? this.toggle.bind(this) : null}
            aria-labelledby={labelID}
          >
            <slot name="header"></slot>
            {this.contentSlot && this.iconPosition === 'inline'
              ? this.iconFragment(labelID)
              : []}
          </div>
          <div class="me-accordion-item__content" ref={el => this.content = el}>
            <div class="me-accordion-item__content-padding">
              <slot name="content" />
            </div>
          </div>
        </div>
        {this.contentSlot && this.iconPosition === 'aside'
          ? this.iconFragment(labelID)
          : []}
      </Host>
    );
  }

  private iconFragment(labelID: string) {
    return (
      <div
        class="me-accordion-item__icon"
        role="button"
        tabindex="0"
        onClick={this.iconPosition === 'inline' ? null : this.toggle.bind(this)}
        aria-labelledby={labelID}
      >
        <span class="me-accordion-item__chevron me-accordion-item__chevron--down">
          <i class="fa-solid fa-chevron-down"></i>
        </span>
        <span class="me-accordion-item__chevron me-accordion-item__chevron--up">
          <i class="fa-solid fa-chevron-up"></i>
        </span>
      </div>
    );
  }

  private updateContentMaxHeight() {
    this.content.style.maxHeight = this.isCollapsed
      ? null
      : this.content.scrollHeight + 'px';
  }

  private enableAnimations() {
    setTimeout(() => this.isAnimated = true);
  }
}
